/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { Fragment } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiCard, useEuiTheme } from '@elastic/eui'

import type { DeploymentSearchResponse } from '@modules/cloud-api/v1/types'
import { withSmallErrorBoundary } from '@modules/cui/Boundary'
import history from '@modules/utils/history'

import { getRegionId } from '@/lib/stackDeployments/selectors/fundamentals'

import StackDeploymentHealthProblems from '../../StackDeploymentHealthProblems'
import DeploymentProvider from '../Deployment/DeploymentProvider'
import DeploymentStackIcons from '../Deployment/DeploymentStackIcons'
import DeploymentConfigurations from '../Deployment/DeploymentConfigurations'
import { deploymentUrl } from '../../../lib/urlBuilder'

import Description from './Description'
import Title from './Title'

import type { FunctionComponent } from 'react'

type Props = {
  deployment: DeploymentSearchResponse
}

const DeploymentCard: FunctionComponent<Props> = ({ deployment }) => {
  const { id } = deployment
  const regionId = getRegionId({ deployment })
  const href = deploymentUrl(id)
  const {
    euiTheme,
    euiTheme: {
      breakpoint: { xl: euiBreakpointXL },
    },
  } = useEuiTheme()
  // a stack deployment may not have a region if it has no associated resources
  const displayHealthProblems = Boolean(regionId)

  return (
    <EuiCard
      data-test-id='deploymentCard'
      className='deploymentCard'
      textAlign='left'
      titleSize='xs'
      css={css({
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        minHeight: '275px',
        transition: `all 150ms ${euiTheme.animation.resistance}`,
        [`@media (min-width: ${euiBreakpointXL}px)`]: {
          minHeight: '300px',
        },
      })}
      href={href}
      onClick={(e) => {
        history.push(href)

        // we need both of these to prevent bubbling up to the link
        // and we need the link because UX of being an actual link (cmd+click, etc.)
        e.preventDefault()
        e.stopPropagation()
      }}
      title={<Title deployment={deployment} />}
      titleElement='span'
      description={<Description deployment={deployment} />}
      footer={
        <Fragment>
          {displayHealthProblems && (
            <StackDeploymentHealthProblems
              deployment={deployment}
              hideLinks={true}
              hideHelpText={true}
              spacerAfter={true}
              ignoreEndOfLifeProblems={true}
              hideHealthApiResults={true}
            />
          )}

          <EuiFlexGroup
            gutterSize='m'
            alignItems='flexEnd'
            justifyContent='spaceBetween'
            responsive={false}
          >
            <EuiFlexItem grow={false}>
              <DeploymentStackIcons deployment={deployment} />
            </EuiFlexItem>

            <EuiFlexItem grow={false}>
              <DeploymentProvider regionId={regionId} />
            </EuiFlexItem>
          </EuiFlexGroup>
        </Fragment>
      }
    >
      <EuiFlexGroup wrap={true}>
        <DeploymentConfigurations deployment={deployment} />
      </EuiFlexGroup>
    </EuiCard>
  )
}

export default withSmallErrorBoundary(DeploymentCard)
